/**
 * Meta Pixel tracking utility with Stape CAPI Gateway support
 * Handles both client-side pixel tracking and server-side conversion API
 */

import { getStableFmeId } from './fmeIdentity';

// Browser detection
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Configuration with explicit environment variables
const META_CONFIG = {
  pixelId: process.env.GATSBY_META_PIXEL_ID || '498446987016519',
  gatewayUrl: process.env.GATSBY_STAPE_CAPI_ENDPOINT || 'https://capig.mckinley.com',
  testEventCode: process.env.GATSBY_META_TEST_EVENT_CODE || null,
  enableDebug: process.env.NODE_ENV === 'development',
};

// SHA-256 hashing function for PII
const sha256Hash = async (text) => {
  if (!text) return null;
  
  // Normalize the text according to Meta requirements
  const normalized = text.toString().toLowerCase().trim();
  
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    // Use Web Crypto API
    const msgBuffer = new TextEncoder().encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } else {
    // Fallback - in production, consider using a library
    console.warn('SHA-256 hashing unavailable - using unhashed value');
    return normalized;
  }
};

// Get Meta cookies for better matching
const getMetaCookies = () => {
  if (!isBrowser) return {};
  
  const cookies = {};
  
  // Get _fbp cookie
  const fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
  if (fbpMatch) {
    cookies.fbp = fbpMatch[1];
  }
  
  // Get _fbc cookie or construct from fbclid
  const fbcMatch = document.cookie.match(/_fbc=([^;]+)/);
  if (fbcMatch) {
    cookies.fbc = fbcMatch[1];
  } else {
    // Try to construct from fbclid in URL
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (fbclid) {
      const timestamp = Math.floor(Date.now() / 1000);
      cookies.fbc = `fb.1.${timestamp}.${fbclid}`;
    }
  }
  
  return cookies;
};


// Helper to get user data for enhanced matching
const getUserData = async () => {
  const fmeId = getStableFmeId();
  const metaCookies = getMetaCookies();
  
  // Basic user data structure for Meta CAPI
  const userData = {
    external_id: await sha256Hash(fmeId), // Hash the FME ID
    client_ip_address: null, // Will be set server-side
    client_user_agent: isBrowser ? window.navigator.userAgent : null,
    fbp: metaCookies.fbp || null,
    fbc: metaCookies.fbc || null,
  };

  // Add any additional user data if available (must be hashed)
  // Example: if you collect email/phone in forms
  // userData.em = await sha256Hash(email);
  // userData.ph = await sha256Hash(phone.replace(/\D/g, ''));
  
  return userData;
};

// Helper to get event metadata with data processing options
const getEventMetadata = () => {
  const metadata = {
    event_time: Math.floor(Date.now() / 1000), // Unix timestamp
    event_source_url: isBrowser ? window.location.href : '',
    action_source: 'website',
  };

  // Add session/referrer data if available
  if (isBrowser) {
    metadata.referrer_url = document.referrer;
  }
  
  // Add data processing options for CCPA compliance
  // LDU = Limited Data Use
  const isCaliforniaUser = () => {
    // Implement your logic to detect CA users
    // This is a placeholder
    return false;
  };
  
  if (isCaliforniaUser()) {
    metadata.data_processing_options = ['LDU'];
    metadata.data_processing_options_country = 1; // USA
    metadata.data_processing_options_state = 1000; // California
  }

  return metadata;
};

// Generate unique event ID for deduplication
const generateEventId = (eventName) => {
  return `${eventName}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
};

// Send event to Stape CAPI Gateway with retry logic
const sendToStapeGateway = async (eventName, parameters, userData, metadata, eventId, retryCount = 0) => {
  if (!isBrowser || !META_CONFIG.gatewayUrl) {
    return;
  }

  try {
    const payload = {
      event_name: eventName,
      event_time: metadata.event_time,
      event_source_url: metadata.event_source_url,
      action_source: metadata.action_source,
      user_data: userData,
      custom_data: parameters,
      event_id: eventId, // Same ID as client-side
    };
    
    // Add data processing options if present
    if (metadata.data_processing_options) {
      payload.data_processing_options = metadata.data_processing_options;
      payload.data_processing_options_country = metadata.data_processing_options_country;
      payload.data_processing_options_state = metadata.data_processing_options_state;
    }

    const body = JSON.stringify({
      data: [payload],
      test_event_code: META_CONFIG.testEventCode,
    });

    // Use sendBeacon for better reliability on page unload
    if (navigator.sendBeacon && parameters._useBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(META_CONFIG.gatewayUrl, blob);
      if (!sent) throw new Error('sendBeacon failed');
      return;
    }

    // Regular fetch with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

    const response = await fetch(META_CONFIG.gatewayUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
      signal: controller.signal,
      keepalive: true, // Better for page unload
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    // Check if this is a CORS error
    const isCorsError = error.name === 'TypeError' && 
                       (error.message.includes('CORS') || 
                        error.message.includes('Network request failed') ||
                        error.message.includes('Failed to fetch'));
    
    // Don't retry CORS errors as they won't resolve with retries
    if (isCorsError) {
      if (META_CONFIG.enableDebug) {
        console.warn('Stape CAPI Gateway CORS error - server configuration needed:', error.message);
      }
      return; // Exit without retrying
    }
    
    // Retry logic with exponential backoff for non-CORS errors
    if (retryCount < 2 && !parameters._noRetry) {
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
      if (META_CONFIG.enableDebug) {
        console.warn(`Stape CAPI retry ${retryCount + 1} after ${delay}ms:`, error);
      }
      setTimeout(() => {
        sendToStapeGateway(eventName, parameters, userData, metadata, eventId, retryCount + 1);
      }, delay);
    } else if (META_CONFIG.enableDebug) {
      console.error('Stape CAPI Gateway error:', error);
    }
  }
};

/**
 * Track Meta Pixel event with both client-side and server-side tracking
 * @param {string} eventName - The event name (e.g., 'Lead', 'ViewContent', 'AddToCart')
 * @param {object} parameters - Event parameters
 * @param {object} options - Additional options like custom user data
 */
export const trackMetaEvent = async (eventName, parameters = {}) => {
  if (!isBrowser) {
    return;
  }

  // Generate single event ID for both client and server
  const eventId = generateEventId(eventName);
  
  // Get user and event metadata
  const userData = await getUserData();
  const metadata = getEventMetadata();

  // Client-side pixel tracking with event ID
  if (typeof window.fbq === 'function') {
    const clientParams = {
      ...parameters,
      eventID: eventId, // Include event ID for deduplication
    };
    window.fbq('track', eventName, clientParams);
    
    if (META_CONFIG.enableDebug) {
      console.log(`📊 Meta Pixel Client Event: ${eventName}`, { eventId, parameters: clientParams });
    }
  }

  // Server-side CAPI tracking via Stape Gateway
  sendToStapeGateway(eventName, parameters, userData, metadata, eventId);
};

/**
 * Track standard Meta events
 */
export const metaPixel = {
  // Configuration getter
  getConfig: () => META_CONFIG,
  
  // Page view is automatically tracked in gatsby-ssr.js
  
  // Track lead generation (e.g., form submission)
  trackLead: (parameters = {}) => {
    trackMetaEvent('Lead', {
      content_name: 'Property Inquiry',
      ...parameters,
    });
  },

  // Track when user views property/floorplan details
  trackViewContent: (contentId, contentType = 'property', parameters = {}) => {
    trackMetaEvent('ViewContent', {
      content_ids: [contentId],
      content_type: contentType,
      content_category: 'Real Estate',
      ...parameters,
    });
  },

  // Track search actions
  trackSearch: (searchString, parameters = {}) => {
    trackMetaEvent('Search', {
      search_string: searchString,
      ...parameters,
    });
  },

  // Track apply button clicks (use beacon for reliability)
  trackInitiateCheckout: (propertyId, parameters = {}) => {
    trackMetaEvent('InitiateCheckout', {
      content_ids: [propertyId],
      content_type: 'property',
      _useBeacon: true, // Use sendBeacon for page navigation
      ...parameters,
    });
  },

  // Track completed application submissions or lease signings
  trackPurchase: (propertyId, value, parameters = {}) => {
    trackMetaEvent('Purchase', {
      content_ids: [propertyId],
      content_type: 'property',
      value: value,
      currency: 'USD',
      ...parameters,
    });
  },

  // Track when user saves/favorites a property
  trackAddToWishlist: (propertyId, value, parameters = {}) => {
    trackMetaEvent('AddToWishlist', {
      content_ids: [propertyId],
      content_type: 'property',
      value: value,
      currency: 'USD',
      ...parameters,
    });
  },

  // Track account creation or application completion
  trackCompleteRegistration: (parameters = {}) => {
    trackMetaEvent('CompleteRegistration', {
      content_name: 'Property Application',
      status: true,
      ...parameters,
    });
  },

  // Track contact form submissions (different from Lead)
  trackContact: (propertyId, contactMethod, parameters = {}) => {
    trackMetaEvent('Contact', {
      content_ids: [propertyId],
      content_type: 'property',
      contact_method: contactMethod, // 'email', 'phone', 'chat'
      ...parameters,
    });
  },

  // Custom event tracking for non-standard events
  trackCustom: (eventName, parameters = {}) => {
    trackMetaEvent(eventName, parameters);
  },

  // Specific custom events for property management
  trackScheduleTour: (propertyId, tourType = 'in-person', parameters = {}) => {
    trackMetaEvent('ScheduleTour', {
      content_ids: [propertyId],
      content_type: 'property',
      tour_type: tourType, // 'in-person', 'virtual', 'self-guided'
      ...parameters,
    });
  },
};

// Export for testing
export { getUserData, getEventMetadata, sendToStapeGateway, sha256Hash, getMetaCookies, generateEventId };